import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type { LueftungBestand } from '../../api';
import { Fachaktionen } from '../Fachaktionen';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  Baum, ContentSection, EmptyState, ErrorState, FilterBar, KpiCard, KpiReihe, LoadingState, Vorbehalte, formatiereZahl,
  useFilterInUrl, type BaumKnoten, type FilterDefinition,
} from '../../muster';
import { LUEFTUNG_ABSCHNITTE } from './abschnitte';
import { bestandKnoten, traegerKnotenId, type Bestandsknoten, type Traeger } from './bestandregeln';
import { Anlagenseite } from './LueftungAnlage';
import { Bauteilliste, Bauteilseite } from './LueftungBauteile';
import { Tabellenaktionen } from './LueftungTabelle';
import './lueftung.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LÜFTUNGSBESTAND: ANLAGEN, VERBUNDNETZE UND EINBAUTEILE (TV6c)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Links der Baum (Geschoss → Anlage, Verbundnetze, nicht zugeordnete
 * Geschosse), rechts der gewählte Träger: zuerst die Anlage mit ihren
 * Stammdaten, dann ihre Bauteile, dann ein Bauteil mit seinen Feldern. Bei
 * 6.622 Bauteilen wäre eine flache Liste nicht benutzbar - und die Antwort
 * läge über dem nginx-Proxypuffer; die Bauteile eines Trägers kommen deshalb
 * erst bei seiner Wahl.
 *
 * Bis TV6c eine Datei mit 2.746 Zeilen (6 rohe Tabellen, 22 `.marke`,
 * Kappungen 8/6, `onClick` an Zeilen und am Dialoghintergrund). Jetzt in
 * Bausteinen unter `seiten/lueftung/` (Anlage, Bauteile, Datenblatt, Feld,
 * Tabelle, Regeln) auf den Mustern. Die SCHREIB- UND EINLESEWEGE sind
 * unverändert - belegt durch die Charakterisierung
 * `Lueftung.schreibwege.test.tsx`, die am alten Stand geschrieben und grün war
 * und hier unverändert grün läuft, und durch die Integration
 * `LueftungSchreibwegeTests` gegen die echten Routen.
 */
export function Lueftung() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [bestand, setBestand] = useState<LueftungBestand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [gewaehlt, setGewaehlt] = useState<Traeger | null>(null);
  // Das gewählte Bauteil (noch im Zustand, nicht in der Adresse).
  const [bauteilId, setBauteilId] = useState<string | null>(null);
  // Beim Wählen einer Anlage zuerst ihre Stammdaten, nicht die Bauteilliste: Die Anlage ist das führende Objekt.
  const [zeigeAnlage, setZeigeAnlage] = useState(true);

  const laden = useCallback(() => {
    setFehler(null);
    api.lueftung(projekt.id).then(setBestand).catch(setFehler);
  }, [api, projekt.id]);

  useEffect(() => { laden(); }, [laden]);

  const filter = useMemo<FilterDefinition[]>(() => [
    { schluessel: 'suche', beschriftung: 'Anlage oder AKS', art: 'text' },
  ], []);
  const [werte, setWerte] = useFilterInUrl(filter);
  const knoten = useMemo(() => (bestand
    ? bestandKnoten(bestand, typeof werte.suche === 'string' ? werte.suche : '')
    : []), [bestand, werte]);

  const waehle = (k: BaumKnoten<Bestandsknoten>) => {
    if (k.daten?.art !== 'traeger') return;
    setGewaehlt(k.daten.traeger);
    setBauteilId(null);
    setZeigeAnlage(true);
  };

  const leer = !!bestand && bestand.anlagen.length === 0;
  const bauteile = (bestand?.anlagen ?? []).reduce((s, a) => s + a.bauteile, 0);
  const ohneAks = (bestand?.anlagen ?? []).reduce((s, a) => s + a.ohneAks, 0);
  const konflikte = (bestand?.anlagen ?? []).filter((a) => a.ebenenkonflikt).length;

  return (
    <>
      <PageHeader
        titel="Lüftungsbestand"
        beschreibung="Anlagen, Verbundnetze und Einbauteile aus der Lüftungsaufnahme - mit Datenpflege, Tabelle und Herstellerunterlagen."
        meta={bestand ? `${formatiereZahl(bestand.anlagen.length, 0)} Anlagen · ${formatiereZahl(bauteile, 0)} Bauteile` : undefined}
        aktionen={<Tabellenaktionen api={api} projektId={projekt.id} umfang={gewaehlt} neuLaden={laden} />}
      />

      {/* Lesen und Schreiben über Aufträge an die Verarbeitungsmaschinen (Revit). */}
      <Fachaktionen
        api={api}
        rolleKey="lueftung"
        leseArt={30}
        schreibArt={31}
        zusatzArten={[{
          art: 32,
          text: 'Kanalnetz lesen',
          titel: 'Kanäle, Formteile, Flexkanäle, Einbauteile, Auslässe und ihre Anschlüsse lesen - rund 42.000 Elemente, '
            + 'etwa zehn bis fünfzehn Minuten. Die Geräte und Einbauteile aus „Aus Modell lesen“ bleiben unberührt.',
        }]}
      />

      {!bestand && fehler === null && <LoadingState text="Lüftungsbestand wird abgerufen …" zeilen={8} />}
      {fehler !== null && (
        <ErrorState meldung="Der Lüftungsbestand konnte nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={laden} />
      )}

      {leer && (
        <EmptyState
          titel="Noch kein Lüftungsbestand."
          erklaerung="Er entsteht aus einem Auftrag mit Lüftungsaufnahme - oben „Aus Modell lesen“."
        />
      )}

      {bestand && !leer && (
        <>
          <Vorbehalte eintraege={konflikte > 0 ? [{
            art: 'sonstiges',
            text: `Bei ${formatiereZahl(konflikte, 0)} Anlage(n) weicht das Geschoss im Namen von der Revit-Ebene ab. Das ist `
              + 'ein Hinweis und kein Fehler: GDG_OKRB trägt sowohl das 6. Obergeschoss als auch die Dachplattform, der '
              + 'Anlagenname unterscheidet sie.',
          }] : []}
          />

          <KpiReihe>
            <KpiCard titel="Anlagen" wert={bestand.anlagen.length} />
            <KpiCard titel="Bauteile" wert={bauteile} />
            <KpiCard titel="ohne AKS" wert={ohneAks} vergleich="keine Anlagenkennung am Bauteil" />
            <KpiCard titel="Verbundnetze" wert={bestand.verbundnetze.length} />
            <KpiCard titel="nicht zugeordnet" wert={bestand.nichtZugeordnet.length} vergleich="Geschosse mit Bauteilen ohne Anlage" />
          </KpiReihe>

          <div className="lue-teilung">
            <div className="lue-teilung-baum">
              <ContentSection
                titel="Gliederung"
                beschreibung="Geschoss aufklappen, Anlage wählen; Verbundnetze und nicht zugeordnete Bauteile stehen eigens."
                werkzeuge={<FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={1} />}
              >
                <Baum<Bestandsknoten>
                  name="Anlagen, Verbundnetze und nicht zugeordnete Geschosse"
                  knoten={knoten}
                  gewaehlt={traegerKnotenId(gewaehlt)}
                  // Verbundnetze und nicht zugeordnete Geschosse standen vor TV6c immer offen da - so bleibt es.
                  anfangsOffen={['g:netze', 'g:offen']}
                  beiWahl={waehle}
                  leer={<span>Keine Anlage passt zur Suche.</span>}
                />
              </ContentSection>
            </div>
            <div className="lue-teilung-detail">
              {bauteilId ? (
                <Bauteilseite api={api} bauteilId={bauteilId} projektId={projekt.id} zurueck={() => setBauteilId(null)} />
              ) : gewaehlt?.art === 'anlage' && zeigeAnlage ? (
                <Anlagenseite api={api} projektId={projekt.id} anlageId={gewaehlt.id} zuBauteilen={() => setZeigeAnlage(false)} />
              ) : gewaehlt ? (
                <Bauteilliste
                  api={api}
                  projektId={projekt.id}
                  traeger={gewaehlt}
                  oeffnen={setBauteilId}
                  zurAnlage={gewaehlt.art === 'anlage' ? () => setZeigeAnlage(true) : undefined}
                />
              ) : (
                <ContentSection titel="Nachweis">
                  <EmptyState klein titel="Noch keine Anlage gewählt." erklaerung="Links ein Geschoss aufklappen und eine Anlage wählen." />
                </ContentSection>
              )}
            </div>
          </div>
        </>
      )}

      {/* Die übrigen Seiten der Lüftung - aus der Gliederung (S1.1), nicht als zweite Liste von Hand. */}
      <ContentSection titel="Weitere Seiten der Lüftung">
        <ul className="lue-seiten">
          {LUEFTUNG_ABSCHNITTE.flatMap((a) => a.eintraege).map((e) => (
            <li key={e.schluessel}>
              <Link to={`/projekte/${projekt.id}/${e.pfad}`}>{e.name}</Link>
              {e.hinweis && <span className="lue-leise"> - {e.hinweis}</span>}
            </li>
          ))}
        </ul>
      </ContentSection>
    </>
  );
}
